import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { lazy, Suspense } from "react";
import { Spinner } from "@/components/ui/spinner";

// Loaded on open. The create form pulls in RoundSpecificForm and the rest of
// the round-control tree — a large import for a tray most operators never open,
// and one that would otherwise be dragged into the league wizard's own graph.
const CreateRoundConfiguration = lazy(
  () =>
    import("components/control/dashboard/round-configuration-manage/CreateRoundConfiguration"),
);

/**
 * Authoring a Round Preset without leaving league setup.
 *
 * A facility with no presets can't use the picker at all, and sending the
 * operator to Settings mid-wizard means abandoning a part-built league. This
 * embeds the existing create form, so there is one authoring path and one place
 * its validation lives.
 *
 * The form owns its own submit and posts through the same mutation the Round
 * Presets screen uses, which merges into the entity slice the picker reads —
 * so a preset created here shows up in the dropdown without a refetch.
 */
export function CreateRoundPresetSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto sm:max-w-3xl"
      >
        <SheetHeader>
          <SheetTitle className="poppins font-semibold tracking-tight">
            New Round Preset
          </SheetTitle>
          <SheetDescription>
            Saved for this facility and reusable on any league or ad-hoc round.
            Once saved, it&rsquo;s selected for this league automatically.
          </SheetDescription>
        </SheetHeader>
        <div className="pb-6">
          {open && (
            <Suspense
              fallback={
                <div className="flex justify-center py-10">
                  <Spinner />
                </div>
              }
            >
              <CreateRoundConfiguration singleColumn isLeague />
            </Suspense>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
